<template>
  <div>
    <!-- Dashboard metrics - Using standard B-Card components -->
    <b-row class="match-height mb-2">
      <b-col
        xl="3"
        md="6"
        sm="12"
      >
        <b-card class="text-center bg-light-primary">
          <div class="mb-1">
            <feather-icon
              icon="TruckIcon"
              size="21"
              class="text-primary"
            />
          </div>
          <h2 class="font-weight-bolder">
            {{ totalCars || 0 }}
          </h2>
          <p class="card-text">
            Total des voitures
          </p>
        </b-card>
      </b-col>
      <b-col
        xl="3"
        md="6"
        sm="12"
      >
        <b-card class="text-center bg-light-success">
          <div class="mb-1">
            <feather-icon
              icon="CheckCircleIcon"
              size="21"
              class="text-success"
            />
          </div>
          <h2 class="font-weight-bolder">
            {{ activeCount }}
          </h2>
          <p class="card-text">
            Voitures actives
          </p>
        </b-card>
      </b-col>
      <b-col
        xl="3"
        md="6"
        sm="12"
      >
        <b-card class="text-center bg-light-warning">
          <div class="mb-1">
            <feather-icon
              icon="UserIcon"
              size="21"
              class="text-warning"
            />
          </div>
          <h2 class="font-weight-bolder">
            {{ assignedCount }}
          </h2>
          <p class="card-text">
            Avec conducteurs
          </p>
        </b-card>
      </b-col>
      <b-col
        xl="3"
        md="6"
        sm="12"
      >
        <b-card class="text-center bg-light-info">
          <div class="mb-1">
            <feather-icon
              icon="CalendarIcon"
              size="21"
              class="text-info"
            />
          </div>
          <h2 class="font-weight-bolder">
            {{ averageYear }}
          </h2>
          <p class="card-text">
            Année moyenne
          </p>
        </b-card>
      </b-col>
    </b-row>

    <!-- Advanced filters -->
    <b-card class="mb-2">
      <b-card-title>Filtres avancés</b-card-title>
      <b-row>
        <b-col
          md="6"
          sm="12"
        >
          <b-form-group label="Rechercher">
            <b-form-input
              v-model="filters.search"
              v-debounce:500ms="getCars"
              placeholder="Rechercher par matricule, marque..."
              class="filter-input"
            />
          </b-form-group>
        </b-col>
        <b-col
          md="6"
          sm="12"
        >
          <b-form-group label="État">
            <v-select
              v-model="filters.active"
              :options="statusOptions"
              placeholder="Tous"
              :clearable="true"
              label="label"
              class="select-search-option"
              :reduce="option => option.value"
              @input="getCars"
            />
          </b-form-group>
        </b-col>
      </b-row>

      <!-- Additional filters (toggleable) -->
      <div v-if="showMoreFilters">
        <b-row>
          <b-col
            md="6"
            sm="12"
          >
            <b-form-group label="Marque">
              <b-form-input
                v-model="filters.brand"
                v-debounce:500ms="getCars"
                placeholder="Filtrer par marque"
                class="filter-input"
              />
            </b-form-group>
          </b-col>
          <b-col
            md="6"
            sm="12"
          >
            <b-form-group label="Couleur">
              <b-form-input
                v-model="filters.color"
                v-debounce:500ms="getCars"
                placeholder="Filtrer par couleur"
                class="filter-input"
              />
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col
            md="6"
            sm="12"
          >
            <b-form-group label="Année">
              <div class="d-flex align-items-center">
                <b-form-input
                  v-model="filters.yearMin"
                  v-debounce:500ms="getCars"
                  placeholder="Min"
                  type="number"
                  min="1900"
                  max="2100"
                  class="mr-2 filter-input"
                />
                <span class="mx-1">à</span>
                <b-form-input
                  v-model="filters.yearMax"
                  v-debounce:500ms="getCars"
                  placeholder="Max"
                  type="number"
                  min="1900"
                  max="2100"
                  class="ml-2 filter-input"
                />
              </div>
            </b-form-group>
          </b-col>

          <b-col
            md="6"
            sm="12"
          >
            <b-form-group label="Conducteur assigné">
              <v-select
                v-model="filters.hasDriver"
                :options="driverOptions"
                placeholder="Tous"
                :clearable="true"
                label="label"
                class="select-search-option"
                :reduce="option => option.value"
                @input="getCars"
              />
            </b-form-group>
          </b-col>
        </b-row>

        <!-- Advanced filters section -->
        <b-row>
          <b-col
            md="6"
            sm="12"
          >
            <b-form-group label="Statut technique">
              <v-select
                v-model="filters.status"
                :options="carStatusOptions"
                placeholder="Tous les statuts"
                :clearable="true"
                label="label"
                class="select-search-option"
                :reduce="option => option.value"
                @input="getCars"
              />
            </b-form-group>
          </b-col>
          <b-col
            md="6"
            sm="12"
          >
            <b-form-group label="Date d'immatriculation">
              <b-form-datepicker
                v-model="filters.registrationDate"
                placeholder="Sélectionner une date"
                :date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric' }"
                class="mb-2 filter-input"
                @input="getCars"
              />
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col
            md="6"
            sm="12"
          >
            <b-form-group label="Type de carburant">
              <v-select
                v-model="filters.fuelType"
                :options="fuelTypeOptions"
                placeholder="Tous les types"
                :clearable="true"
                label="label"
                class="select-search-option"
                :reduce="option => option.value"
                @input="getCars"
              />
            </b-form-group>
          </b-col>
          <b-col
            md="6"
            sm="12"
          >
            <b-form-group label="Transmission">
              <v-select
                v-model="filters.transmission"
                :options="transmissionOptions"
                placeholder="Toutes les transmissions"
                :clearable="true"
                label="label"
                class="select-search-option"
                :reduce="option => option.value"
                @input="getCars"
              />
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col
            md="6"
            sm="12"
          >
            <b-form-group label="Capacité (m³)">
              <div class="d-flex align-items-center">
                <b-form-input
                  v-model="filters.capacityMin"
                  v-debounce:500ms="getCars"
                  placeholder="Min"
                  type="number"
                  step="0.1"
                  min="0"
                  class="mr-2 filter-input"
                />
                <span class="mx-1">à</span>
                <b-form-input
                  v-model="filters.capacityMax"
                  v-debounce:500ms="getCars"
                  placeholder="Max"
                  type="number"
                  step="0.1"
                  min="0"
                  class="ml-2 filter-input"
                />
              </div>
            </b-form-group>
          </b-col>
          <b-col
            md="6"
            sm="12"
          >
            <b-form-group label="Kilométrage">
              <div class="d-flex align-items-center">
                <b-form-input
                  v-model="filters.mileageMin"
                  v-debounce:500ms="getCars"
                  placeholder="Min"
                  type="number"
                  min="0"
                  class="mr-2 filter-input"
                />
                <span class="mx-1">à</span>
                <b-form-input
                  v-model="filters.mileageMax"
                  v-debounce:500ms="getCars"
                  placeholder="Max"
                  type="number"
                  min="0"
                  class="ml-2 filter-input"
                />
              </div>
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col
            md="6"
            sm="12"
          >
            <b-form-group label="Utilisation dans les feuilles de route">
              <v-select
                v-model="filters.inUseInRoadmap"
                :options="roadmapUsageOptions"
                placeholder="Tous"
                :clearable="true"
                label="label"
                class="select-search-option"
                :reduce="option => option.value"
                @input="getCars"
              />
            </b-form-group>
          </b-col>
          <b-col
            md="6"
            sm="12"
          >
            <b-form-group label="Entretien prévu">
              <v-select
                v-model="filters.maintenanceDue"
                :options="maintenanceOptions"
                placeholder="Tous"
                :clearable="true"
                label="label"
                class="select-search-option"
                :reduce="option => option.value"
                @input="getCars"
              />
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col
            md="6"
            sm="12"
          >
            <b-form-group label="Expiration d'assurance">
              <b-form-datepicker
                v-model="filters.insuranceExpiry"
                placeholder="Sélectionner une date"
                :date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric' }"
                class="mb-2 filter-input"
                @input="getCars"
              />
            </b-form-group>
          </b-col>
          <b-col
            md="6"
            sm="12"
          >
            <b-form-group label="Expiration contrôle technique">
              <b-form-datepicker
                v-model="filters.technicalInspectionExpiry"
                placeholder="Sélectionner une date"
                :date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric' }"
                class="mb-2 filter-input"
                @input="getCars"
              />
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col md="12">
            <b-form-group label="Nom du conducteur">
              <b-form-input
                v-model="filters.driverName"
                v-debounce:500ms="getCars"
                placeholder="Rechercher par nom du conducteur"
                class="filter-input"
              />
            </b-form-group>
          </b-col>
        </b-row>
      </div>

      <div class="d-flex justify-content-between mt-1">
        <div>
          <b-button
            variant="outline-secondary"
            size="sm"
            @click="resetFilters"
          >
            <span>Réinitialiser</span>
          </b-button>
        </div>
        <b-button
          variant="outline-primary"
          size="sm"
          @click="toggleMoreFilters"
        >
          <span>{{ showMoreFilters ? 'Moins de filtres' : 'Plus de filtres' }}</span>
        </b-button>
      </div>
    </b-card>

    <!-- Header section with title and badge -->
    <div class="custom-search d-flex justify-content-between mb-2">
      <div class="d-flex align-items-center">
        <h3 class="mb-0 mr-2">
          Liste des Voitures
        </h3>
        <b-badge
          variant="primary"
          pill
          class="badge-round"
        >
          {{ totalCars || 0 }}
        </b-badge>
      </div>
      <div class="d-flex">
        <b-form-group
          v-if="user.role==='admin'"
          class="mb-0"
        >
          <b-button
            variant="outline-primary"
            class="mr-1"
            @click="$refs.quickCreateCarModal.show()"
          >
            <feather-icon icon="ZapIcon" />
            <span class="ml-1">Création Rapide</span>
          </b-button>
          <b-button
            :to="{ name: 'car-add' }"
            variant="primary"
            class="d-inline-block"
          >
            <feather-icon icon="PlusIcon" />
            <span class="ml-1">Ajouter une voiture</span>
          </b-button>
        </b-form-group>
      </div>
    </div>

    <!-- Loading states with skeletons -->
    <div
      v-if="isLoading"
      class="mb-2"
    >
      <b-card>
        <b-skeleton-table
          :rows="5"
          :columns="6"
          :table-props="{ bordered: true, striped: true }"
        />
      </b-card>
    </div>

    <!-- Enhanced card grid -->
    <div
      v-else-if="!isLoading && cars.length > 0"
      class="car-card-grid"
    >
      <b-row>
        <b-col
          v-for="car in cars"
          :key="car.id"
          xl="4"
          md="6"
          sm="12"
          class="mb-2"
        >
          <b-card
            no-body
            class="car-card h-100 shadow-sm border-0"
          >
            <div
              class="status-indicator"
              :class="car.is_active ? 'bg-success' : 'bg-danger'"
            />
            <b-card-body class="p-2">
              <div class="d-flex justify-content-between align-items-start mb-1">
                <div class="flex-grow-1 pr-1">
                  <h4 class="mb-0 text-primary font-weight-bolder text-truncate">
                    {{ car.serial_number }}
                  </h4>
                  <div class="text-muted small text-truncate">
                    {{ car.brand }} {{ car.model }}
                  </div>
                </div>
                <b-badge
                  pill
                  :variant="car.is_active ? 'light-success' : 'light-danger'"
                  class="flex-shrink-0"
                >
                  {{ car.is_active ? 'Actif' : 'Inactif' }}
                </b-badge>
              </div>

              <div class="car-details mt-2">
                <div class="detail-item mb-1">
                  <feather-icon
                    icon="CalendarIcon"
                    size="14"
                    class="mr-1 text-muted"
                  />
                  <span>Année: <strong>{{ car.year || 'N/A' }}</strong></span>
                </div>
                <div class="detail-item mb-1">
                  <feather-icon
                    icon="DropletIcon"
                    size="14"
                    class="mr-1 text-muted"
                  />
                  <span>Couleur: <strong>{{ car.color || 'N/A' }}</strong></span>
                </div>
                <div class="detail-item mb-1">
                  <feather-icon
                    icon="UserIcon"
                    size="14"
                    class="mr-1 text-muted"
                  />
                  <span class="text-truncate">Conducteur:
                    <strong v-if="car.default_driver_details">
                      {{ car.default_driver_details.first_name }} {{ car.default_driver_details.last_name }}
                    </strong>
                    <span
                      v-else
                      class="text-muted italic"
                    >Non assigné</span>
                  </span>
                </div>
              </div>
            </b-card-body>

            <b-card-footer class="bg-transparent border-top-0 p-2 d-flex justify-content-between align-items-center">
              <div class="actions">
                <b-button
                  v-b-tooltip.hover
                  variant="flat-info"
                  size="sm"
                  class="btn-icon rounded-circle"
                  title="Voir détails"
                  :to="{ name: 'car-show', params: { id: car.id }}"
                >
                  <feather-icon
                    icon="EyeIcon"
                    size="16"
                  />
                </b-button>
                <b-button
                  v-if="user.role === 'admin'"
                  v-b-tooltip.hover
                  variant="flat-warning"
                  size="sm"
                  class="btn-icon rounded-circle ml-1"
                  title="Modifier"
                  :to="{ name: 'car-edit', params: { id: car.id }}"
                >
                  <feather-icon
                    icon="EditIcon"
                    size="16"
                  />
                </b-button>
                <b-button
                  v-if="user.role === 'admin'"
                  v-b-tooltip.hover
                  variant="flat-danger"
                  size="sm"
                  class="btn-icon rounded-circle ml-1"
                  title="Supprimer"
                  @click="confirmDelete(car.id)"
                >
                  <feather-icon
                    icon="TrashIcon"
                    size="16"
                  />
                </b-button>
              </div>

              <div v-if="user.role === 'admin'">
                <b-button
                  v-if="car.is_active"
                  v-b-tooltip.hover
                  variant="light-danger"
                  size="sm"
                  title="Désactiver"
                  @click="confirmToggleStatus(car.id, false)"
                >
                  <feather-icon
                    icon="XCircleIcon"
                    size="14"
                  />
                </b-button>
                <b-button
                  v-else
                  v-b-tooltip.hover
                  variant="light-success"
                  size="sm"
                  title="Activer"
                  @click="confirmToggleStatus(car.id, true)"
                >
                  <feather-icon
                    icon="CheckCircleIcon"
                    size="14"
                  />
                </b-button>
              </div>
            </b-card-footer>
          </b-card>
        </b-col>
      </b-row>

      <!-- Pagination for cars cards -->
      <div class="d-flex justify-content-between align-items-center mt-2 flex-wrap pagination-container">
        <div class="d-flex align-items-center mb-1">
          <span class="text-nowrap mr-1">Afficher</span>
          <b-form-select
            v-model="pageLength"
            :options="['25', '50', '100', '500']"
            size="sm"
            class="page-length-select"
            @change="getCars"
          />
          <span class="text-nowrap ml-1 text-muted small">sur {{ totalRecords }} véhicules</span>
        </div>
        <b-pagination
          v-model="currentPage"
          :total-rows="totalRecords"
          :per-page="pageLength"
          first-number
          last-number
          prev-class="prev-item"
          next-class="next-item"
          class="mb-1"
          @input="getCars"
        >
          <template #prev-text>
            <feather-icon
              icon="ChevronLeftIcon"
              size="18"
            />
          </template>
          <template #next-text>
            <feather-icon
              icon="ChevronRightIcon"
              size="18"
            />
          </template>
        </b-pagination>
      </div>
    </div>

    <!-- Status confirmation modal -->
    <b-modal
      id="confirm-status-modal"
      ref="confirmStatusModal"
      :title="statusModalTitle"
      ok-variant="primary"
      cancel-variant="outline-secondary"
      @ok="handleStatusConfirmed"
      @cancel="cancelStatusChange"
    >
      <p>{{ statusModalMessage }}</p>
    </b-modal>

    <!-- Delete confirmation modal -->
    <b-modal
      id="confirm-delete-modal"
      ref="confirmDeleteModal"
      title="Confirmer la suppression"
      ok-variant="danger"
      ok-title="Supprimer"
      cancel-variant="outline-secondary"
      @ok="handleDeleteConfirmed"
      @cancel="cancelDelete"
    >
      <p>Êtes-vous sûr de vouloir supprimer cette voiture ? Cette action est irréversible.</p>
    </b-modal>

    <!-- Bulk action confirmation modal -->
    <b-modal
      id="confirm-bulk-modal"
      ref="confirmBulkModal"
      :title="bulkModalTitle"
      ok-variant="primary"
      cancel-variant="outline-secondary"
      @ok="handleBulkConfirmed"
      @cancel="cancelBulkChange"
    >
      <p>{{ bulkModalMessage }}</p>
      <ul v-if="selectedRows.length <= 5">
        <li
          v-for="row in selectedRows"
          :key="row.id"
        >
          {{ row.serial_number }} ({{ row.brand }})
        </li>
      </ul>
      <p v-else>
        {{ selectedRows.length }} voitures seront affectées.
      </p>
    </b-modal>

    <!-- Quick create car modal -->
    <quick-create-car-modal
      ref="quickCreateCarModal"
      @car-created="getCars"
    />

    <div
      id="cars-table-help"
      class="sr-only"
    >
      Ce tableau présente la liste des véhicules du parc logistique. Utilisez les filtres et la recherche pour affiner les résultats.
    </div>
  </div>
</template>

<script>
import {
  BPagination,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BButton,
  BCard,
  BCardTitle,
  BRow,
  BCol,
  BBadge,
  BSkeletonTable,
  BModal,
} from 'bootstrap-vue'
import axios from 'axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import vSelect from 'vue-select'
import storeAuth from '@/store/store'
import QuickCreateCarModal from '@/components/modals/QuickCreateCarModal.vue'

export default {
  components: {
    BPagination,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BButton,
    BCard,
    BCardTitle,
    BRow,
    BCol,
    BBadge,
    BSkeletonTable,
    BModal,
    vSelect,
    QuickCreateCarModal,
  },
  data() {
    return {
      isLoading: false,
      pageLength: 100,
      user: {},
      columns: [
        {
          label: 'Matricule',
          field: 'serial_number',
          filterOptions: {
            enabled: true,
            placeholder: 'chercher matricule',
          },
          tdClass: 'font-weight-bold',
        },
        {
          label: 'Marque',
          field: 'brand',
          filterOptions: {
            enabled: true,
            placeholder: 'Chercher marque',
          },
        },
        {
          label: 'Modèle',
          field: 'model',
          filterOptions: {
            enabled: true,
            placeholder: 'Chercher modèle',
          },
        },
        {
          label: 'Année',
          field: 'year',
          type: 'number',
          filterOptions: {
            enabled: true,
          },
          tdClass: 'text-center',
        },
        {
          label: 'Couleur',
          field: 'color',
          filterOptions: {
            enabled: true,
            placeholder: 'Chercher couleur',
          },
        },
        {
          label: 'Conducteur',
          field: row => (row.default_driver_details ? row.default_driver_details.username : 'Non assigné'),
          filterOptions: {
            enabled: true,
            placeholder: 'Chercher conducteur',
          },
          thClass: 'min-width-200',
          tdClass: 'min-width-200',
          sortable: true,
        },
        {
          label: 'État',
          field: 'is_active',
          filterOptions: {
            enabled: true,
          },
          thClass: 'text-center',
          tdClass: 'text-center',
        },
        {
          label: 'Action',
          field: 'action',
          thClass: 'text-center',
          tdClass: 'text-center',
        },
      ],
      cars: [],
      searchTerm: '',
      showMoreFilters: false,
      totalRecords: 0,
      currentPage: 1,
      toggleStatusLoading: null,
      carToToggle: null,
      newStatus: null,
      statusModalTitle: '',
      statusModalMessage: '',
      carToDelete: null,
      selectedRows: [],
      bulkModalTitle: '',
      bulkModalMessage: '',
      bulkAction: null,

      // Filters
      filters: {
        search: '',
        active: null,
        brand: '',
        color: '',
        yearMin: null,
        yearMax: null,
        hasDriver: null,
        status: null,
        fuelType: null,
        transmission: null,
        capacityMin: null,
        capacityMax: null,
        mileageMin: null,
        mileageMax: null,
        registrationDate: null,
        insuranceExpiry: null,
        technicalInspectionExpiry: null,
        inUseInRoadmap: null,
        maintenanceDue: null,
        driverName: '',
      },

      // Filter options
      statusOptions: [
        { value: true, label: 'Actif' },
        { value: false, label: 'Inactif' },
      ],
      driverOptions: [
        { value: true, label: 'Assigné' },
        { value: false, label: 'Non assigné' },
      ],
      carStatusOptions: [
        { value: 'available', label: 'Disponible' },
        { value: 'in_use', label: 'En utilisation' },
        { value: 'maintenance', label: 'En maintenance' },
        { value: 'out_of_service', label: 'Hors service' },
      ],
      fuelTypeOptions: [
        { value: 'Gasoline', label: 'Essence' },
        { value: 'Diesel', label: 'Diesel' },
        { value: 'Electric', label: 'Électrique' },
        { value: 'Hybrid', label: 'Hybride' },
        { value: 'CNG', label: 'Gaz naturel' },
        { value: 'LPG', label: 'GPL' },
      ],
      transmissionOptions: [
        { value: 'Automatic', label: 'Automatique' },
        { value: 'Manual', label: 'Manuelle' },
        { value: 'Semi-automatic', label: 'Semi-automatique' },
        { value: 'CVT', label: 'CVT' },
      ],
      roadmapUsageOptions: [
        { value: true, label: 'Utilisé dans une feuille de route' },
        { value: false, label: 'Non utilisé dans une feuille de route' },
      ],
      maintenanceOptions: [
        { value: true, label: 'Maintenance prévue ou expiration imminente' },
        { value: false, label: 'Aucune maintenance prévue prochainement' },
      ],
    }
  },
  computed: {
    // Add the missing totalCars computed property
    totalCars() {
      return this.cars.length
    },

    activeCount() {
      return this.cars.filter(car => car.is_active).length
    },

    inactiveCount() {
      return this.cars.filter(car => !car.is_active).length
    },

    assignedCount() {
      return this.cars.filter(car => car.default_driver !== null).length
    },

    unassignedCount() {
      return this.cars.filter(car => car.default_driver === null).length
    },

    averageYear() {
      const carsWithYear = this.cars.filter(car => car.year)
      if (carsWithYear.length === 0) return 'N/A'

      const sum = carsWithYear.reduce((acc, car) => acc + car.year, 0)
      return Math.round(sum / carsWithYear.length)
    },
    hasActiveFilters() {
      return (
        this.filters.search !== ''
        || this.filters.active !== null
        || this.filters.brand !== ''
        || this.filters.color !== ''
        || this.filters.yearMin !== null
        || this.filters.yearMax !== null
        || this.filters.hasDriver !== null
        || this.filters.status !== null
        || this.filters.fuelType !== null
        || this.filters.transmission !== null
        || this.filters.capacityMin !== null
        || this.filters.capacityMax !== null
        || this.filters.mileageMin !== null
        || this.filters.mileageMax !== null
        || this.filters.registrationDate !== null
        || this.filters.insuranceExpiry !== null
        || this.filters.technicalInspectionExpiry !== null
        || this.filters.inUseInRoadmap !== null
        || this.filters.maintenanceDue !== null
        || this.filters.driverName !== ''
      )
    },
  },
  created() {
    this.user = storeAuth.state.user
    this.getCars()
  },
  methods: {
    // Toggle more filters
    toggleMoreFilters() {
      this.showMoreFilters = !this.showMoreFilters
    },

    // Get cars with applied filters
    async getCars() {
      this.isLoading = true
      try {
        let url = '/api/cars/'

        // Add query params for filters
        const params = new URLSearchParams()

        if (this.filters.search) {
          params.append('serial_number__icontains', this.filters.search)
        }

        if (this.filters.active !== null) {
          params.append('is_active', this.filters.active)
        }

        if (this.filters.brand) {
          params.append('brand__icontains', this.filters.brand)
        }

        if (this.filters.color) {
          params.append('color__icontains', this.filters.color)
        }

        if (this.filters.yearMin) {
          params.append('year__gte', this.filters.yearMin)
        }

        if (this.filters.yearMax) {
          params.append('year__lte', this.filters.yearMax)
        }

        if (this.filters.hasDriver !== null) {
          params.append('has_driver', this.filters.hasDriver)
        }

        if (this.filters.status) {
          params.append('status', this.filters.status)
        }

        if (this.filters.fuelType) {
          params.append('fuel_type', this.filters.fuelType)
        }

        if (this.filters.transmission) {
          params.append('transmission', this.filters.transmission)
        }

        if (this.filters.capacityMin) {
          params.append('capacity__gte', this.filters.capacityMin)
        }

        if (this.filters.capacityMax) {
          params.append('capacity__lte', this.filters.capacityMax)
        }

        if (this.filters.mileageMin) {
          params.append('mileage__gt', this.filters.mileageMin)
        }

        if (this.filters.mileageMax) {
          params.append('mileage__lt', this.filters.mileageMax)
        }

        if (this.filters.registrationDate) {
          params.append('registration_date', this.formatDate(this.filters.registrationDate))
        }

        if (this.filters.insuranceExpiry) {
          params.append('insurance_expiry__gte', this.formatDate(this.filters.insuranceExpiry))
        }

        if (this.filters.technicalInspectionExpiry) {
          params.append('technical_inspection_expiry__gte', this.formatDate(this.filters.technicalInspectionExpiry))
        }

        if (this.filters.inUseInRoadmap !== null) {
          params.append('in_use_in_roadmap', this.filters.inUseInRoadmap)
        }

        if (this.filters.maintenanceDue !== null) {
          params.append('maintenance_due', this.filters.maintenanceDue)
        }

        if (this.filters.driverName) {
          params.append('driver_name', this.filters.driverName)
        }

        // Pagination params
        params.append('limit', this.pageLength)
        params.append('offset', (this.currentPage - 1) * this.pageLength)

        // Append params to URL if any exist
        if (params.toString()) url = `${url}?${params.toString()}`

        const { data } = await axios.get(url)

        // Handle paginated response from backend if implemented
        if (data.results) {
          this.cars = data.results
          this.totalRecords = data.count
        } else {
          this.cars = data
          this.totalRecords = data.length
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching cars:', error)
        this.showToast('danger', 'top-center', 'Erreur lors du chargement des voitures')
      } finally {
        this.isLoading = false
      }
    },

    // Format date for API requests
    formatDate(date) {
      if (!date) return null

      // Convert to YYYY-MM-DD format
      if (typeof date === 'string' && date.includes('-')) {
        return date // Already in correct format
      }

      const d = new Date(date)
      let month = `${d.getMonth() + 1}`
      let day = `${d.getDate()}`
      const year = d.getFullYear()

      if (month.length < 2) month = `0${month}`
      if (day.length < 2) day = `0${day}`

      return [year, month, day].join('-')
    },

    // Reset filters
    resetFilters() {
      this.filters = {
        search: '',
        active: null,
        brand: '',
        color: '',
        yearMin: null,
        yearMax: null,
        hasDriver: null,
        status: null,
        fuelType: null,
        transmission: null,
        capacityMin: null,
        capacityMax: null,
        mileageMin: null,
        mileageMax: null,
        registrationDate: null,
        insuranceExpiry: null,
        technicalInspectionExpiry: null,
        inUseInRoadmap: null,
        maintenanceDue: null,
        driverName: '',
      }
      this.getCars()
    },

    // Delete car
    confirmDelete(id) {
      this.carToDelete = id
      this.$refs.confirmDeleteModal.show()
    },

    cancelDelete() {
      this.carToDelete = null
    },

    async handleDeleteConfirmed() {
      if (!this.carToDelete) return

      try {
        this.isLoading = true
        await axios.delete(`/api/cars/delete/${this.carToDelete}/`)
        this.getCars()
        this.showToast('success', 'top-center', 'Voiture supprimée avec succès')
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error deleting car:', error)
        this.showToast('danger', 'top-center', 'Erreur lors de la suppression de la voiture')
      } finally {
        this.isLoading = false
        this.carToDelete = null
      }
    },

    // Status toggle methods
    confirmToggleStatus(id, makeActive) {
      this.carToToggle = id
      this.newStatus = makeActive

      this.statusModalTitle = makeActive ? 'Activer la voiture' : 'Désactiver la voiture'
      this.statusModalMessage = makeActive
        ? 'Êtes-vous sûr de vouloir activer cette voiture?'
        : 'Êtes-vous sûr de vouloir désactiver cette voiture?'

      this.$refs.confirmStatusModal.show()
    },

    cancelStatusChange() {
      this.carToToggle = null
      this.newStatus = null
    },

    handleStatusConfirmed() {
      if (this.carToToggle !== null) {
        this.toggleStatus(this.carToToggle, this.newStatus)
      }
    },

    async toggleStatus(id, makeActive) {
      try {
        this.toggleStatusLoading = id
        await axios.post(`/api/cars/toggle-status/${id}/`, { is_active: makeActive })

        // Update the item in the cars array to avoid a full reload
        const index = this.cars.findIndex(car => car.id === id)
        if (index !== -1) {
          this.cars[index].is_active = makeActive
        }

        this.showToast(
          'success',
          'top-center',
          `La voiture a été ${makeActive ? 'activée' : 'désactivée'} avec succès`,
        )
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error toggling car status:', error)
        this.showToast('danger', 'top-center', 'Erreur lors du changement de statut')
      } finally {
        this.toggleStatusLoading = null
        this.carToToggle = null
        this.newStatus = null
      }
    },

    // Bulk actions
    onSelectedRowsChange(params) {
      this.selectedRows = params.selectedRows
    },

    clearSelection() {
      this.$refs.carsTable.unselectAllRows()
      this.selectedRows = []
    },

    bulkActivate() {
      this.bulkModalTitle = 'Activer les voitures sélectionnées'
      this.bulkModalMessage = 'Êtes-vous sûr de vouloir activer toutes les voitures sélectionnées?'
      this.bulkAction = 'activate'
      this.$refs.confirmBulkModal.show()
    },

    bulkDeactivate() {
      this.bulkModalTitle = 'Désactiver les voitures sélectionnées'
      this.bulkModalMessage = 'Êtes-vous sûr de vouloir désactiver toutes les voitures sélectionnées?'
      this.bulkAction = 'deactivate'
      this.$refs.confirmBulkModal.show()
    },

    cancelBulkChange() {
      this.bulkAction = null
    },

    async handleBulkConfirmed() {
      if (!this.bulkAction) return

      const isActivate = this.bulkAction === 'activate'
      const ids = this.selectedRows.map(row => row.id)

      try {
        this.isLoading = true
        await axios.post('/api/cars/bulk-toggle-status/', {
          ids,
          is_active: isActivate,
        })

        // Update the status in the local data
        ids.forEach(id => {
          const index = this.cars.findIndex(car => car.id === id)
          if (index !== -1) {
            this.cars[index].is_active = isActivate
          }
        })

        this.clearSelection()
        this.showToast(
          'success',
          'top-center',
          `${ids.length} voitures ont été ${isActivate ? 'activées' : 'désactivées'} avec succès`,
        )
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error performing bulk action:', error)
        this.showToast('danger', 'top-center', 'Erreur lors de la mise à jour des statuts')
      } finally {
        this.isLoading = false
        this.bulkAction = null
      }
    },

    // Pagination methods
    onPageChange(params) {
      this.currentPage = params.currentPage
      this.getCars()
    },

    onPerPageChange(params) {
      this.pageLength = params.currentPerPage
      this.currentPage = 1
      this.getCars()
    },

    // Toast notification
    showToast(variant, position, text) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: 'Notification',
          icon: variant === 'success' ? 'CheckIcon' : 'AlertTriangleIcon',
          text,
          variant,
        },
      }, {
        position,
      })
    },
  },
}
</script>

<style lang="scss">
// Car Card Grid Styles
.car-card {
  transition: transform 0.2s, box-shadow 0.2s;
  position: relative;
  overflow: hidden;
  border-radius: 12px !important;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba($color: #FF0D02, $alpha: 0.15) !important;
  }

  .status-indicator {
    position: absolute;
    left: 0;
    top: 0;
    width: 6px;
    height: 100%;
  }

  .car-details {
    .detail-item {
      font-size: 0.85rem;
      display: flex;
      align-items: center;
      color: #5e5873;
    }
  }

  .btn-flat-info:hover { background-color: rgba($color: #00cfe8, $alpha: 0.12); }
  .btn-flat-warning:hover { background-color: rgba($color: #ff9f43, $alpha: 0.12); }
  .btn-flat-danger:hover { background-color: rgba($color: #ea5455, $alpha: 0.12); }
}

.pagination-container {
  background-color: #f8f8f8;
  padding: 1rem;
  border-radius: 8px;
}

.page-length-select {
  width: auto;
  display: inline-block;
}

.italic {
  font-style: italic;
}

.text-truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
